/**
 * 语速工具函数
 * API格式：取值范围[-50, 100]，100代表2.0倍速，-50代表0.5倍速，0代表1.0倍速（默认）
 */

// 倍速值到API值的映射
const SPEED_TO_API_MAP: Record<number, number> = {
  0.5: -50,
  0.6: -40,
  0.7: -30,
  0.8: -20,
  0.9: -10,
  1.0: 0,
  1.1: 10,
  1.2: 20,
  1.3: 30,
  1.4: 40,
  1.5: 50,
  1.6: 60,
  1.7: 70,
  1.8: 80,
  1.9: 90,
  2.0: 100,
};

// API值到倍速值的映射（反向映射）
const API_TO_SPEED_MAP: Record<number, number> = Object.entries(
  SPEED_TO_API_MAP
).reduce((acc, [speed, api]) => {
  acc[api] = parseFloat(speed);
  return acc;
}, {} as Record<number, number>);

// 可用的语速选项（API格式）
export const SPEED_OPTIONS = [-20, -10, 0, 20, 50]; // 对应 0.8x, 0.9x, 1.0x, 1.2x, 1.5x

/**
 * 将倍速值转换为API格式值
 * @param speed 倍速值（0.5-2.0）或API值（-50到100）
 * @returns API格式值（-50到100）
 */
export function speedToApi(speed: number): number {
  // 如果已经是API格式（-50到100的整数），直接返回
  if (speed >= -50 && speed <= 100 && Number.isInteger(speed)) {
    return speed;
  }

  // 如果是倍速值，查找映射或计算
  if (speed >= 0.5 && speed <= 2.0) {
    // 优先使用映射表（保留一位小数）
    const roundedSpeed = Math.round(speed * 10) / 10;
    if (SPEED_TO_API_MAP[roundedSpeed] !== undefined) {
      return SPEED_TO_API_MAP[roundedSpeed];
    }
    // 如果映射表中没有，使用公式计算并限制在有效范围内
    return Math.max(-50, Math.min(100, Math.round((speed - 1.0) * 100)));
  }

  // 默认返回0（1.0倍速）
  return 0;
}

/**
 * 将API格式值转换为倍速值
 * @param apiValue API格式值（-50到100）
 * @returns 倍速值（0.5-2.0）
 */
export function apiToSpeed(apiValue: number): number {
  if (API_TO_SPEED_MAP[apiValue] !== undefined) {
    return API_TO_SPEED_MAP[apiValue];
  }
  // 如果映射表中没有，使用公式计算
  return 1.0 + apiValue / 100;
}

/**
 * 获取倍速值的显示文本
 * @param apiValue API格式值
 * @returns 显示文本，如 "0.8x"
 */
export function getSpeedDisplayText(apiValue: number): string {
  const speed = apiToSpeed(apiValue);
  return `${speed.toFixed(1)}x`;
}
